import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";
import { Plus, MoreVertical, Pencil, Trash2, Copy, GripVertical, BarChart2, AlertCircle, Eye, EyeOff, Layers, History, Loader2, Database, Info } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { ReportFreshnessText } from "@/components/report-freshness-text";
import { MetricWidget } from "@/components/metrics/MetricWidget";
import { MetricsSectionNav } from "@/components/metrics/MetricsSectionNav";
import { MetricConditionBuilder } from "@/components/metrics/MetricConditionBuilder";
import { buildConditionDescription, buildDescriptionSuggestion } from "@/lib/metric-description";
import type { FieldDefinition, ConditionLeaf, ConditionTree } from "@/lib/metric-description";
import { useGlossaryLookup } from "@/hooks/use-glossary";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Fixed display order for category sections
const CATEGORY_DISPLAY_ORDER = ["Population", "Behavior", "Medical", "Evaluation", "Intake", "Holds", "Other"];

interface Metric {
  id: string;
  label: string;
  description: string | null;
  dataset: string;
  conditions: ConditionTree;
  resultType: string;
  targetPages: string[];
  displayFields: string[];
  defaultSortField: string | null;
  defaultSortDirection: string | null;
  sortOrder: number;
  status: "draft" | "published";
  category: string;
  chartEnabled: boolean;
  defaultView: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  snapshotDays: number;
}

interface RegistryResponse {
  datasets: Array<{
    key: string;
    label: string;
    fields: FieldDefinition[];
  }>;
}

const TARGET_PAGE_OPTIONS = [
  { value: "dashboard", label: "Dashboard" },
  { value: "animals", label: "/animals page" },
  { value: "location_snapshot", label: "Location Snapshots (dashboard detail panels)" },
];

const EMPTY_TREE: ConditionTree = { operator: "and", conditions: [] };

interface MetricFormState {
  label: string;
  description: string;
  dataset: string;
  conditions: ConditionLeaf[];
  targetPages: string[];
  displayFields: string[];
  defaultSortField: string;
  defaultSortDirection: "asc" | "desc";
  chartEnabled: boolean;
  defaultView: "table" | "chart";
}

function getDefaultForm(): MetricFormState {
  return {
    label: "",
    description: "",
    dataset: "ANIMALS",
    conditions: [{ field: "is_current_resident", operator: "is_true", value: null }],
    targetPages: ["dashboard"],
    displayFields: [],
    defaultSortField: "",
    defaultSortDirection: "desc",
    chartEnabled: false,
    defaultView: "table",
  };
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function SortableMetricCard({
  metric,
  onEdit,
  onDuplicate,
  onDelete,
  onPublish,
  onUnpublish,
  onBackfill,
}: {
  metric: Metric;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onBackfill: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: metric.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const conditionCount = metric.conditions?.conditions?.length ?? 0;

  return (
    <div ref={setNodeRef} style={style} data-testid={`card-metric-def-${metric.id}`}>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2 flex-wrap space-y-0">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <button
              {...attributes}
              {...listeners}
              className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`handle-metric-${metric.id}`}
              aria-label="Drag to reorder"
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-sm font-semibold">{metric.label}</CardTitle>
                <Badge variant={metric.status === "published" ? "default" : "secondary"} className="text-xs">
                  {metric.status === "published" ? "Published" : "Draft"}
                </Badge>
                {metric.category && (
                  <Badge variant="secondary" className="text-xs" data-testid={`badge-category-${metric.id}`}>
                    {metric.category}
                  </Badge>
                )}
                {metric.status === "published" && (
                  <Badge
                    variant="outline"
                    className="text-xs flex items-center gap-1"
                    data-testid={`badge-snapshot-days-${metric.id}`}
                  >
                    <Database className="h-2.5 w-2.5" />
                    {metric.snapshotDays > 0 ? `${metric.snapshotDays} days` : "No snapshot data"}
                  </Badge>
                )}
              </div>
              {metric.description && (
                <CardDescription className="text-xs mt-0.5 line-clamp-2">{metric.description}</CardDescription>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`menu-metric-${metric.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit} data-testid={`menu-edit-${metric.id}`}>
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate} data-testid={`menu-duplicate-${metric.id}`}>
                <Copy className="h-4 w-4 mr-2" /> Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {metric.status === "draft" ? (
                <DropdownMenuItem onClick={onPublish} data-testid={`menu-publish-${metric.id}`}>
                  <Eye className="h-4 w-4 mr-2" /> Publish
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onClick={onUnpublish} data-testid={`menu-unpublish-${metric.id}`}>
                    <EyeOff className="h-4 w-4 mr-2" /> Unpublish
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onBackfill} data-testid={`menu-backfill-${metric.id}`}>
                    <History className="h-4 w-4 mr-2" /> Backfill history
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:text-destructive"
                data-testid={`menu-delete-${metric.id}`}
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {metric.dataset}
            </span>
            <span>{conditionCount} condition{conditionCount !== 1 ? "s" : ""}</span>
            <span className={`flex items-center gap-1 ${metric.chartEnabled ? "" : "opacity-50"}`} data-testid={`badge-trend-${metric.id}`}>
              <BarChart2 className="h-3 w-3" />
              {metric.chartEnabled ? "Trend on" : "No trend"}
            </span>
            {metric.targetPages && metric.targetPages.length > 0 && (
              <span>
                Surfaces: {metric.targetPages
                  .map((p) => TARGET_PAGE_OPTIONS.find((o) => o.value === p)?.label ?? p)
                  .join(", ")}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function MetricsPage() {
  usePageTitle("Counts");
  const { user } = useAuth();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState("live");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState<Metric | null>(null);
  const [form, setForm] = useState<MetricFormState>(getDefaultForm());
  const [showValidation, setShowValidation] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Metric | null>(null);
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [liveCountLoading, setLiveCountLoading] = useState(false);
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [backfillTarget, setBackfillTarget] = useState<Metric | null>(null);
  const [backfillDays, setBackfillDays] = useState("90");
  const [showFieldsDialog, setShowFieldsDialog] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const { data: metricsResponse, isLoading: metricsLoading } = useQuery<{ metrics: Metric[]; total: number }>({
    queryKey: ["/api/metrics"],
  });
  const allMetrics = metricsResponse?.metrics;

  const { data: registry, isLoading: registryLoading } = useQuery<RegistryResponse>({
    queryKey: ["/api/metrics/registry"],
  });

  const publishedMetrics = (allMetrics ?? []).filter((m) => m.status === "published");

  const orderedMetrics = (() => {
    if (!allMetrics) return [];
    if (localOrder.length === 0) return allMetrics;
    const map = new Map(allMetrics.map((m) => [m.id, m]));
    return localOrder.map((id) => map.get(id)).filter(Boolean) as Metric[];
  })();

  useEffect(() => {
    if (!allMetrics) return;
    setLocalOrder((prev) => {
      if (prev.length === 0) return allMetrics.map((m) => m.id);
      const prevSet = new Set(prev);
      const newIds = allMetrics.filter((m) => !prevSet.has(m.id)).map((m) => m.id);
      if (newIds.length === 0) return prev;
      return [...prev, ...newIds];
    });
  }, [allMetrics]);

  const allFields: FieldDefinition[] =
    registry?.datasets?.find((d) => d.key === form.dataset)?.fields ?? [];

  const isLocationSnapshotTarget = form.targetPages.includes("location_snapshot");

  const fields: FieldDefinition[] = isLocationSnapshotTarget
    ? allFields.filter((f) => f.key !== "location")
    : allFields;

  const hasLocationSnapshotConflict =
    isLocationSnapshotTarget && form.conditions.some((c) => c.field === "location");

  const hasDualSurfaceWarning =
    form.targetPages.includes("dashboard") && form.targetPages.includes("location_snapshot");

  const debouncedConditions = useDebounce(form.conditions, 600);

  const previewFetchRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!sheetOpen) return;

    const hasValidConditions = debouncedConditions.length > 0;
    if (!hasValidConditions) {
      setLiveCount(null);
      return;
    }

    previewFetchRef.current?.abort();
    const controller = new AbortController();
    previewFetchRef.current = controller;

    const csrfToken = document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? "";

    setLiveCountLoading(true);
    fetch("/api/metrics/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-XSRF-TOKEN": csrfToken },
      credentials: "include",
      body: JSON.stringify({
        dataset: form.dataset,
        conditions: { operator: "and", conditions: debouncedConditions },
      }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((d) => setLiveCount(d.count ?? 0))
      .catch(() => {})
      .finally(() => setLiveCountLoading(false));

    return () => controller.abort();
  }, [sheetOpen, debouncedConditions, form.dataset]);

  const createMutation = useMutation({
    mutationFn: async (data: { label: string; description?: string; dataset: string; conditions: ConditionTree; targetPages: string[]; displayFields: string[]; defaultSortField?: string | null; defaultSortDirection?: string | null; chartEnabled?: boolean; defaultView?: string; status: string }) =>
      apiRequest("POST", "/api/metrics", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/metrics"] });
      closeSheet();
      toast({ title: "Count created" });
    },
    onError: () => toast({ title: "Failed to create count", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; label: string; description?: string; dataset: string; conditions: ConditionTree; targetPages: string[]; displayFields: string[]; defaultSortField?: string | null; defaultSortDirection?: string | null; chartEnabled?: boolean; defaultView?: string; status: string }) =>
      apiRequest("PATCH", `/api/metrics/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/metrics"] });
      closeSheet();
      toast({ title: "Count updated" });
    },
    onError: () => toast({ title: "Failed to update count", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/metrics/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/metrics"] });
      toast({ title: "Count deleted" });
    },
    onError: () => toast({ title: "Failed to delete count", variant: "destructive" }),
  });

  const publishMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/metrics/${id}/publish`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/metrics"] });
      toast({ title: "Count published" });
    },
    onError: (e: Error) => toast({ title: e.message || "Cannot publish count", variant: "destructive" }),
  });

  const unpublishMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/metrics/${id}/unpublish`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/metrics"] });
      toast({ title: "Count unpublished" });
    },
    onError: () => toast({ title: "Failed to unpublish", variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (orderedIds: string[]) =>
      apiRequest("POST", "/api/metrics/reorder", {
        items: orderedIds.map((id, i) => ({ id, sortOrder: i + 1 })),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/metrics"] }),
  });

  const backfillMutation = useMutation({
    mutationFn: async ({ id, days }: { id: string; days: number }) =>
      apiRequest("POST", `/api/metrics/${id}/backfill`, { days }),
    onSuccess: (data: { datesProcessed: number; rowsWritten: number; errors: string[] }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/metrics"] });
      setBackfillTarget(null);
      setBackfillDays("90");
      toast({
        title: "Backfill complete",
        description: `${data.rowsWritten} snapshot rows written across ${data.datesProcessed} dates — dates with no accepted reports are skipped automatically.`,
      });
    },
    onError: (e: Error) => toast({ title: "Backfill failed", description: e.message, variant: "destructive" }),
  });

  function openCreate() {
    setEditingMetric(null);
    setForm(getDefaultForm());
    setShowValidation(false);
    setLiveCount(null);
    setSheetOpen(true);
  }

  function openEdit(metric: Metric) {
    setEditingMetric(metric);
    setForm({
      label: metric.label,
      description: metric.description ?? "",
      dataset: metric.dataset,
      conditions: metric.conditions?.conditions ?? [],
      targetPages: metric.targetPages ?? ["dashboard"],
      displayFields: metric.displayFields ?? [],
      defaultSortField: metric.defaultSortField ?? "",
      defaultSortDirection: (metric.defaultSortDirection as "asc" | "desc") ?? "desc",
      chartEnabled: metric.chartEnabled ?? false,
      defaultView: (metric.defaultView as "table" | "chart") ?? "table",
    });
    setShowValidation(false);
    setLiveCount(null);
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    setEditingMetric(null);
    setForm(getDefaultForm());
    setShowValidation(false);
    setLiveCount(null);
  }

  function handleDuplicate(metric: Metric) {
    setEditingMetric(null);
    setForm({
      label: `${metric.label} (copy)`,
      description: metric.description ?? "",
      dataset: metric.dataset,
      conditions: metric.conditions?.conditions ?? [],
      targetPages: metric.targetPages ?? ["dashboard"],
      displayFields: [],
      defaultSortField: metric.defaultSortField ?? "",
      defaultSortDirection: (metric.defaultSortDirection as "asc" | "desc") ?? "desc",
      // Intentionally reset chart settings on duplicate — copy starts with defaults
      chartEnabled: false,
      defaultView: "table",
    });
    setShowValidation(false);
    setLiveCount(null);
    setSheetOpen(true);
  }

  function isFormValid() {
    return form.label.trim().length > 0 && form.conditions.length > 0 && !hasLocationSnapshotConflict;
  }

  function handleSave(publish: boolean) {
    if (!isFormValid()) {
      setShowValidation(true);
      return;
    }

    const payload = {
      label: form.label.trim(),
      description: form.description.trim() || undefined,
      dataset: form.dataset,
      conditions: { operator: "and" as const, conditions: form.conditions },
      targetPages: form.targetPages,
      displayFields: form.displayFields,
      defaultSortField: form.defaultSortField || null,
      defaultSortDirection: form.defaultSortField ? form.defaultSortDirection : null,
      chartEnabled: form.chartEnabled,
      defaultView: form.chartEnabled ? form.defaultView : "table",
      status: publish ? "published" : "draft",
    };

    if (editingMetric) {
      updateMutation.mutate({ id: editingMetric.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localOrder.indexOf(active.id as string);
    const newIndex = localOrder.indexOf(over.id as string);
    const newOrder = arrayMove(localOrder, oldIndex, newIndex);
    setLocalOrder(newOrder);
    reorderMutation.mutate(newOrder);
  }

  function toggleTargetPage(page: string) {
    setForm((f) => ({
      ...f,
      targetPages: f.targetPages.includes(page)
        ? f.targetPages.filter((p) => p !== page)
        : [...f.targetPages, page],
    }));
  }

  const glossaryLookup = useGlossaryLookup();
  const descriptionPreview = buildConditionDescription(form.conditions, fields);
  const descriptionSuggestion = useMemo(
    () => buildDescriptionSuggestion(form.conditions, glossaryLookup),
    [form.conditions, glossaryLookup],
  );
  const hasIncompleteConditions =
    showValidation && form.conditions.length === 0;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4" data-testid="page-metrics">
      <PageHeader
        breadcrumb={[{ label: "Monitoring" }, { label: "Counts" }]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-metrics">
            <BarChart2 className="w-6 h-6 text-primary" />
            Counts
          </span>
        }
        description="Build named population counts, track how they change over time, and surface them across the dashboard and location panels."
        actions={
          <div className="flex items-center gap-2">
            <ReportFreshnessText reportTypes={["allPets", "interaction"]} testIdPrefix="metrics" />
            {user?.isAdmin && activeTab === "definitions" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowFieldsDialog(true)}
                  data-testid="button-fields-reference"
                >
                  <Info className="h-4 w-4 mr-1" />
                  Fields reference
                </Button>
                <Button onClick={openCreate} data-testid="button-new-metric">
                  <Plus className="h-4 w-4 mr-1" />
                  New Count
                </Button>
              </>
            )}
          </div>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList data-testid="tabs-metrics">
          <TabsTrigger value="live" data-testid="tab-live-view">Published Counts</TabsTrigger>
          {user?.isAdmin && (
            <TabsTrigger value="definitions" data-testid="tab-definitions">Manage Counts</TabsTrigger>
          )}
        </TabsList>

          <TabsContent value="live" className="space-y-4">
            {metricsLoading ? (
              <div className="flex flex-col gap-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-64 w-full rounded-md" />
                ))}
              </div>
            ) : publishedMetrics.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-live-view">
                <BarChart2 className="h-10 w-10 text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No published counts yet</p>
                {user?.isAdmin && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Go to the Manage Counts tab to create and publish counts.
                  </p>
                )}
              </div>
            ) : (() => {
              const sorted = [...publishedMetrics].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
              const byCategory = new Map<string, Metric[]>();
              for (const m of sorted) {
                const cat = m.category || "Other";
                if (!byCategory.has(cat)) byCategory.set(cat, []);
                byCategory.get(cat)!.push(m);
              }
              const presentCategories = CATEGORY_DISPLAY_ORDER.filter((cat) => byCategory.has(cat));
              const extraCategories = [...byCategory.keys()].filter((c) => !CATEGORY_DISPLAY_ORDER.includes(c));
              const allCategories = [...presentCategories, ...extraCategories];
              return (
                <div data-metrics-scroll-container>
                  <MetricsSectionNav categories={allCategories} />
                  {allCategories.map((cat) => (
                    <section
                      key={cat}
                      id={`category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                      className="mb-8 scroll-mt-16"
                    >
                      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3"
                        data-testid={`section-heading-${cat.toLowerCase()}`}>
                        {cat}
                      </h2>
                      <div className="flex flex-col gap-4">
                        {(byCategory.get(cat) ?? []).map((metric) => (
                          <MetricWidget
                            key={metric.id}
                            metricId={metric.id}
                            label={metric.label}
                            description={metric.description}
                            displayFields={metric.displayFields ?? []}
                            defaultSortField={metric.defaultSortField}
                            defaultSortDirection={metric.defaultSortDirection}
                            chartEnabled={metric.chartEnabled}
                            defaultView={metric.defaultView}
                            resultType={metric.resultType}
                            conditions={metric.conditions}
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              );
            })()}
          </TabsContent>

          {user?.isAdmin && (
            <TabsContent value="definitions" className="space-y-2">
              {metricsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 w-full rounded-md" />
                  ))}
                </div>
              ) : orderedMetrics.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-definitions">
                  <BarChart2 className="h-10 w-10 text-muted-foreground mb-4" />
                  <p className="text-lg font-medium text-muted-foreground">No counts yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Click "New Count" to create your first count.</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={localOrder}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {orderedMetrics.map((metric) => (
                        <SortableMetricCard
                          key={metric.id}
                          metric={metric}
                          onEdit={() => openEdit(metric)}
                          onDuplicate={() => handleDuplicate(metric)}
                          onDelete={() => setDeleteTarget(metric)}
                          onPublish={() => publishMutation.mutate(metric.id)}
                          onUnpublish={() => unpublishMutation.mutate(metric.id)}
                          onBackfill={() => { setBackfillTarget(metric); setBackfillDays("90"); }}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </TabsContent>
          )}
        </Tabs>

      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto flex flex-col gap-0">
          <SheetHeader className="pb-4">
            <SheetTitle>{editingMetric ? "Edit Count" : "New Count"}</SheetTitle>
            <SheetDescription>
              Define the conditions that determine which animals match this count.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto space-y-6 px-1 pb-6">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="metric-label">
                  Label <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="metric-label"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Dogs available for adoption"
                  data-testid="input-metric-label"
                  className={showValidation && !form.label.trim() ? "ring-2 ring-destructive" : ""}
                />
                {showValidation && !form.label.trim() && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Label is required
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="metric-description">Description</Label>
                <Textarea
                  id="metric-description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description shown on the count card…"
                  rows={2}
                  data-testid="input-metric-description"
                />
                {descriptionSuggestion && (
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-muted-foreground flex-1">
                      <span className="font-medium text-foreground">Suggested:</span> {descriptionSuggestion}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm((f) => ({ ...f, description: descriptionSuggestion }))}
                      data-testid="button-use-suggested-description"
                    >
                      Use this
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Dataset</Label>
                <Select
                  value={form.dataset}
                  onValueChange={(v) => setForm((f) => ({ ...f, dataset: v, conditions: [] }))}
                  disabled={!!editingMetric}
                >
                  <SelectTrigger className="w-[200px]" data-testid="select-metric-dataset">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {registryLoading ? (
                      <SelectItem value="ANIMALS">Animals</SelectItem>
                    ) : (
                      (registry?.datasets ?? [{ key: "ANIMALS", label: "Animals", fields: [] }]).map((d) => (
                        <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">Conditions</p>
                <p className="text-xs text-muted-foreground">All conditions must be true (AND logic). At least one is required.</p>
              </div>

              {registryLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (
                <MetricConditionBuilder
                  key={editingMetric?.id ?? "new"}
                  fields={fields}
                  conditions={form.conditions}
                  onChange={(c) => setForm((f) => ({ ...f, conditions: c }))}
                  showValidation={showValidation}
                />
              )}

              {hasIncompleteConditions && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> At least one complete condition is required
                </p>
              )}

              {descriptionPreview && (
                <div className="p-3 rounded-md bg-muted/50 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Preview: </span>
                  {descriptionPreview}
                </div>
              )}

              {form.conditions.length > 0 && (
                <div className="text-xs text-muted-foreground" data-testid="metric-live-count">
                  {editingMetric ? null : liveCountLoading ? (
                    <span className="animate-pulse">Counting matching animals…</span>
                  ) : liveCount !== null ? (
                    <span>
                      <span className="font-semibold text-foreground">{liveCount}</span> animal{liveCount !== 1 ? "s" : ""} match these conditions
                    </span>
                  ) : null}
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">Surfaces</p>
                <p className="text-xs text-muted-foreground">Where this count appears when published.</p>
              </div>
              <div className="space-y-2">
                {TARGET_PAGE_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`target-${opt.value}`}
                      checked={form.targetPages.includes(opt.value)}
                      onCheckedChange={() => toggleTargetPage(opt.value)}
                      data-testid={`checkbox-target-${opt.value}`}
                    />
                    <Label htmlFor={`target-${opt.value}`} className="font-normal cursor-pointer">
                      {opt.label}
                    </Label>
                  </div>
                ))}
              </div>
              {hasLocationSnapshotConflict && (
                <div className="flex items-start gap-2 p-3 rounded-md border border-destructive/40 bg-destructive/5 text-xs text-destructive" data-testid="warning-location-snapshot-conflict">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Location Snapshot counts are automatically scoped to each location — remove the "Location" condition to save or publish.
                  </span>
                </div>
              )}
              {hasDualSurfaceWarning && (
                <div className="flex items-start gap-2 p-3 rounded-md border bg-muted/50 text-xs text-muted-foreground" data-testid="warning-dual-surface">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    When published to both surfaces, this count will show different numbers in each place — the dashboard shows the full unscoped total across all locations, while each location's panel shows only animals at that location.
                  </span>
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">Widget columns</p>
                <p className="text-xs text-muted-foreground">Choose which columns appear in the count widget. Name is always shown.</p>
              </div>
              <div className="space-y-2">
                {[
                  { value: "location", label: "Location & zone" },
                  { value: "days_in_shelter", label: "Days in shelter" },
                  { value: "adoption_level", label: "Adoption level" },
                  { value: "evaluation_status", label: "Evaluation status" },
                  { value: "on_hold", label: "On hold indicator" },
                ].map((col) => (
                  <div key={col.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`display-field-${col.value}`}
                      checked={form.displayFields.includes(col.value)}
                      onCheckedChange={() =>
                        setForm((f) => ({
                          ...f,
                          displayFields: f.displayFields.includes(col.value)
                            ? f.displayFields.filter((d) => d !== col.value)
                            : [...f.displayFields, col.value],
                        }))
                      }
                      data-testid={`checkbox-display-field-${col.value}`}
                    />
                    <Label htmlFor={`display-field-${col.value}`} className="font-normal cursor-pointer">
                      {col.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">Trend view</p>
                <p className="text-xs text-muted-foreground">Enable a trend/table toggle on the count widget.</p>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="chart-enabled"
                  checked={form.chartEnabled}
                  onCheckedChange={(checked) =>
                    setForm((f) => ({
                      ...f,
                      chartEnabled: !!checked,
                      defaultView: checked ? f.defaultView : "table",
                    }))
                  }
                  data-testid="checkbox-chart-enabled"
                />
                <Label htmlFor="chart-enabled" className="font-normal cursor-pointer">
                  Show trend/table toggle
                </Label>
              </div>
              {form.chartEnabled && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Default view</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={form.defaultView === "table" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setForm((f) => ({ ...f, defaultView: "table" }))}
                      data-testid="button-default-view-table"
                    >
                      Table
                    </Button>
                    <Button
                      type="button"
                      variant={form.defaultView === "chart" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setForm((f) => ({ ...f, defaultView: "chart" }))}
                      data-testid="button-default-view-chart"
                    >
                      Trend
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">Default sort</p>
                <p className="text-xs text-muted-foreground">Controls the initial row order when the widget loads.</p>
              </div>
              <div className="space-y-2">
                <Select
                  value={form.defaultSortField || "__none__"}
                  onValueChange={(v) =>
                    setForm((f) => ({
                      ...f,
                      defaultSortField: v === "__none__" ? "" : v,
                      defaultSortDirection: v === "length_of_stay" ? "desc" : v === "intake_date" ? "asc" : "asc",
                    }))
                  }
                >
                  <SelectTrigger data-testid="select-default-sort-field">
                    <SelectValue placeholder="No default sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No default sort</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                    <SelectItem value="length_of_stay">Length of stay</SelectItem>
                    <SelectItem value="intake_date">Intake date</SelectItem>
                    <SelectItem value="kennel_status">Kennel status</SelectItem>
                    <SelectItem value="adoption_level">Adoption level</SelectItem>
                    <SelectItem value="evaluation_status">Evaluation status</SelectItem>
                  </SelectContent>
                </Select>

                {form.defaultSortField && (() => {
                  const isLos = form.defaultSortField === "length_of_stay";
                  const isDate = form.defaultSortField === "intake_date";
                  const [labelAsc, labelDesc] = isLos
                    ? ["Shortest first", "Longest first"]
                    : isDate
                    ? ["Oldest first", "Newest first"]
                    : ["A → Z", "Z → A"];
                  return (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={form.defaultSortDirection === "asc" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setForm((f) => ({ ...f, defaultSortDirection: "asc" }))}
                        data-testid="button-sort-dir-asc"
                      >
                        {labelAsc}
                      </Button>
                      <Button
                        type="button"
                        variant={form.defaultSortDirection === "desc" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setForm((f) => ({ ...f, defaultSortDirection: "desc" }))}
                        data-testid="button-sort-dir-desc"
                      >
                        {labelDesc}
                      </Button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="border-t pt-4 flex items-center justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={closeSheet}
              disabled={isSaving}
              data-testid="button-cancel-metric"
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleSave(false)}
              disabled={isSaving}
              data-testid="button-save-draft"
            >
              Save as Draft
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={isSaving}
              data-testid="button-publish-metric"
            >
              Publish
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete count?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.label}" will be permanently deleted and removed from all surfaces.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id);
                  setDeleteTarget(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!backfillTarget}
        onOpenChange={(open) => { if (!open) { setBackfillTarget(null); setBackfillDays("90"); } }}
      >
        <DialogContent data-testid="dialog-backfill">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Backfill history
            </DialogTitle>
            <DialogDescription>
              Populate snapshot data for <span className="font-medium text-foreground">{backfillTarget?.label}</span> by re-computing counts from historical reports. Only dates with accepted reports will produce rows — others are skipped automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Duration</label>
              <Select
                value={backfillDays}
                onValueChange={setBackfillDays}
                disabled={backfillMutation.isPending}
              >
                <SelectTrigger data-testid="select-backfill-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="180">Last 180 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {backfillTarget && backfillTarget.snapshotDays > 0 && (
              <p className="text-xs text-muted-foreground">
                This count currently has <span className="font-medium text-foreground">{backfillTarget.snapshotDays} days</span> of snapshot data. Re-running will overwrite existing values with fresh counts.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setBackfillTarget(null); setBackfillDays("90"); }}
              disabled={backfillMutation.isPending}
              data-testid="button-cancel-backfill"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (backfillTarget) {
                  backfillMutation.mutate({ id: backfillTarget.id, days: parseInt(backfillDays, 10) });
                }
              }}
              disabled={backfillMutation.isPending}
              data-testid="button-confirm-backfill"
            >
              {backfillMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Backfilling…
                </>
              ) : (
                "Run backfill"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fields reference dialog */}
      <Dialog open={showFieldsDialog} onOpenChange={setShowFieldsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-fields-reference">
          <DialogHeader>
            <DialogTitle>Count Builder — Fields Reference</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  The Count Builder lets you define custom population counts based on animal data.
                  Each count uses the conditions you choose to filter the shelter's animal records
                  and report a count — for example, "dogs currently available for adoption at Salem."
                </p>
                <div>
                  <p className="font-medium text-foreground mb-1">How trends are built</p>
                  <p>
                    Every time a daily report is accepted, BETI runs each published count against
                    that report's snapshot of the animal population and records the count for that date.
                    The trend plots those daily counts over the selected time window (7, 30, or 90 days).
                    Breakdown options — by species, location, or kennel — split the same count into separate
                    series so you can see how each segment contributes to the total.
                    Comparison modes overlay the equivalent prior period (previous week or previous month)
                    so you can judge whether the number is moving in the right direction.
                  </p>
                </div>
                <p>The fields below are available as filter conditions when building a count.</p>
              </div>
            </DialogDescription>
          </DialogHeader>

          {registryLoading ? (
            <div className="space-y-2 py-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : (() => {
            const allFields = (registry?.datasets ?? []).flatMap((d) => d.fields);
            const TYPE_LABELS: Record<string, string> = {
              enum: "Choice",
              compound_status: "Status",
              boolean: "Yes / No",
              number: "Number",
              date: "Date",
              text: "Text",
            };
            const TYPE_DESCRIPTIONS: Record<string, string> = {
              enum: "Pick one or more values from a fixed list.",
              compound_status: "Filter by a combined status and substatus pairing.",
              boolean: "A flag that is either set (Yes) or not set (No).",
              number: "Compare a numeric value — greater than, less than, or within a range.",
              date: "Filter by date, either relative (within the last N days) or an exact date.",
              text: "Match against one or more specific text values.",
            };
            const TYPE_ORDER = ["enum", "compound_status", "boolean", "number", "date", "text"];
            const grouped = TYPE_ORDER.reduce<Record<string, typeof allFields>>((acc, t) => {
              const group = allFields.filter((f) => f.dataType === t);
              if (group.length > 0) acc[t] = group;
              return acc;
            }, {});
            return (
              <div className="space-y-5 py-1">
                {Object.entries(grouped).map(([type, fields]) => (
                  <div key={type}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {TYPE_LABELS[type] ?? type}
                    </p>
                    {TYPE_DESCRIPTIONS[type] && (
                      <p className="text-xs text-muted-foreground mb-2 mt-0.5">{TYPE_DESCRIPTIONS[type]}</p>
                    )}
                    <div className="rounded-md border divide-y">
                      {fields.map((f) => (
                        <div
                          key={f.key}
                          className="flex items-start gap-3 px-3 py-2.5"
                          data-testid={`field-row-${f.key}`}
                        >
                          <div className="min-w-[160px]">
                            <p className="text-sm font-medium leading-snug">{f.label}</p>
                            <p className="text-xs text-muted-foreground font-mono mt-0.5">{f.key}</p>
                          </div>
                          <p className="text-sm text-muted-foreground leading-snug">{f.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {allFields.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-6">No fields found.</p>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
